import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, message, Timeline } from 'antd';
import { Icons } from 'icons';
import { cloneDeep, debounce, pick } from 'lodash-es';
import { useRouter } from 'views/useRouter';

import { ItemIcon } from 'components/common';
import { createWorkflow } from 'lib/apis/workflow';
import { classnames } from 'lib/classnames';
import useI18n from 'lib/hooks/useI18n';
import useToken from 'lib/hooks/useToken';
import { i18n } from 'lib/i18n';
import {
  ItemType,
  ItemTypeScheme,
  ItemTypeScreenScheme,
  ItemTypeScreenSchemeMapping,
  Screen,
  ScreenScheme,
  Workflow,
  WorkflowScheme,
  WorkflowSchemeConfig,
  WorkspaceTemplate,
} from 'lib/models';

import WorkspaceTemplateContext from './context';
import {
  commonValidate,
  getDifference,
  SAVE_STEP_ENUM,
  SCREEN_ENUM,
  simplifyHierarchy,
  useSaveTimelines,
} from './util';

import styles from './SavePage.module.less';

const cx = classnames(styles);
const screenTypeList = Object.keys(SCREEN_ENUM);

const countName = async (schemeName, name) => {
  const count = await new Parse.Query(schemeName).equalTo('name', name).count();
  if (count > 0) {
    return Promise.reject(i18n.t('validate.haveExistedName.0'));
  }
  return Promise.resolve();
};

const debounceValidate = schemeName => {
  const fn = value => countName(schemeName, value);
  return debounce(fn, 300);
};

const createValidateRules = schemeName => {
  const validator = debounceValidate(schemeName);
  return [
    {
      required: true,
      validator: async (_, value) => {
        const error = commonValidate(value);
        if (error) return Promise.reject(error);
        return validator(value);
      },
    },
  ];
};

const SavePage: React.FC = () => {
  const { t } = useI18n();

  const router = useRouter();
  const { tenant } = useToken();

  const [form] = Form.useForm();

  const {
    setShowSavePage,
    changeMap,
    workspaceTemplate,
    sourceWorkspaceTemplate,
    itemTypeMap,
    workflowList,
    screenList,
  } = useContext(WorkspaceTemplateContext);
  const saveTimelines = useSaveTimelines();

  const [screenDifference, setScreenDifference] = useState({});

  const [, setWorkflowDifference] = useState({});
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});

  const [currentStep, setCurrentStep] = useState(0);

  const currentTimeline = useMemo(() => {
    return saveTimelines[currentStep];
  }, [currentStep, saveTimelines]);

  const showPrev = useMemo(() => {
    if (currentStep <= SAVE_STEP_ENUM.HIERARCHY) return false;
    const { hierarchy, screen, workflow } = changeMap || {};
    if (currentStep === SAVE_STEP_ENUM.SCREEN) return hierarchy;
    if (currentStep === SAVE_STEP_ENUM.WORKFLOW) return hierarchy || screen;
    if (currentStep === SAVE_STEP_ENUM.CONFIRM) return hierarchy || screen || workflow;
  }, [currentStep, changeMap]);

  const hierarchyRules = useMemo(() => {
    return createValidateRules(ItemTypeScheme);
  }, []);

  const itemTypeScreenRules = useMemo(() => {
    return createValidateRules(ItemTypeScreenScheme);
  }, []);

  const workflowRules = useMemo(() => {
    return createValidateRules(WorkflowScheme);
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      const { hierarchy, screen, workflow } = changeMap;
      const values = form.getFieldsValue(true);

      const templateObject = WorkspaceTemplate.createWithoutData(workspaceTemplate.objectId);

      let itemTypeSchemePointer;
      let itemTypeScreenSchemePointer;
      let workflowSchemePointer;

      if (workspaceTemplate?.itemTypeScheme?.objectId) {
        itemTypeSchemePointer = ItemTypeScheme.createWithoutData(workspaceTemplate.itemTypeScheme.objectId).toPointer();
      }

      if (workspaceTemplate?.itemTypeScreenScheme?.objectId) {
        itemTypeScreenSchemePointer = ItemTypeScreenScheme.createWithoutData(
          workspaceTemplate.itemTypeScreenScheme.objectId,
        ).toPointer();
      }

      if (workspaceTemplate?.workflowScheme?.objectId) {
        workflowSchemePointer = WorkflowScheme.createWithoutData(workspaceTemplate.workflowScheme.objectId).toPointer();
      }

      // 类型层级方案保存
      if (hierarchy) {
        const obj = {
          name: values.hierarchyName,
          hierarchy: JSON.stringify(simplifyHierarchy(workspaceTemplate.itemTypeScheme.hierarchy)),
        };

        const itemTypeSchemeObject = new ItemTypeScheme();
        const itemTypeScheme = await itemTypeSchemeObject.save(obj);

        itemTypeSchemePointer = ItemTypeScheme.createWithoutData(itemTypeScheme.id).toPointer();
      }

      // 界面
      if (screen) {
        const screenConfigMap = cloneDeep(workspaceTemplate.itemTypeScreenScheme.screenConfigMap);
        const _screenDifference = cloneDeep(screenDifference);
        const keys = Object.keys(screenConfigMap);
        // 找出复制的界面
        const needCreates = [];
        keys.forEach(key => {
          const current = screenConfigMap[key];
          screenTypeList.forEach(type => {
            const screenType = SCREEN_ENUM[type];
            const screen = current?.[screenType];
            if (screen?.isCopy) {
              needCreates.push({
                screenId: screen.objectId,
                itemType: key,
                screenType,
              });
            }
          });
        });

        // 创建界面
        if (needCreates.length) {
          const screenPromises = needCreates.map(async ({ screenId, screenType, itemType }) => {
            const screenObject = new Screen();
            const targetScreen = screenList.find(screen => screen.objectId === screenId);

            const screen = await screenObject.save(
              pick(targetScreen, [
                'name',
                'layout',
                'config',
                'validations',
                'defaultValuesScript',
                'afterCreatedItemScript',
                'beforeSaveScript',
              ]),
            );

            screenConfigMap[itemType][screenType].objectId = screen.id;
            _screenDifference[itemType][screenType].objectId = screen.id;
          });

          await Promise.all(screenPromises);
        }

        // 创建界面方案
        const screenSchemePromises = Object.keys(_screenDifference).map(async itemType => {
          const current = _screenDifference[itemType];
          const screenSchemeObject = new ScreenScheme();
          screenTypeList.forEach(type => {
            const screenType = SCREEN_ENUM[type];
            const screen = current?.[screenType];
            if (screen) {
              screenSchemeObject.set(screenType, Screen.createWithoutData(screen.objectId).toPointer());
            }
          });
          screenSchemeObject.set('name', values.screenSchemeName[itemType]);

          const screenScheme = await screenSchemeObject.save();
          screenConfigMap[itemType].objectId = screenScheme.id;
        });

        await Promise.all(screenSchemePromises);

        // 默认界面方案
        const defaultSchemePointer = ScreenScheme.createWithoutData(screenConfigMap.__default__.objectId).toPointer();
        // 创建类型界面方案mapping

        const mappings = [];
        const mappingPromises = [];
        keys.forEach(itemType => {
          if (itemType === '__default__') {
            return;
          }
          const mapping = new ItemTypeScreenSchemeMapping();
          mapping.set('itemType', ItemType.createWithoutData(itemType).toPointer());
          mapping.set('screenScheme', ScreenScheme.createWithoutData(screenConfigMap[itemType].objectId).toPointer());

          const func = async () => {
            const result = await mapping.save();
            mappings.push(ItemTypeScreenSchemeMapping.createWithoutData(result.id).toPointer());
          };
          mappingPromises.push(func());
        });

        await Promise.all(mappingPromises);

        // 创建类型界面方案
        const itemTypeScreenSchemeObject = new ItemTypeScreenScheme();
        itemTypeScreenSchemeObject.set('name', values.itemTypeScreenName);
        itemTypeScreenSchemeObject.set('defaultScreenScheme', defaultSchemePointer);
        if (mappings.length) {
          itemTypeScreenSchemeObject.set('itemTypeScreenSchemeMappings', mappings);
        }

        const itemTypeScreenScheme = await itemTypeScreenSchemeObject.save();

        itemTypeScreenSchemePointer = ItemTypeScreenScheme.createWithoutData(itemTypeScreenScheme.id).toPointer();
      }

      // 工作流
      if (workflow) {
        const workflowConfigMap = cloneDeep(workspaceTemplate.workflowScheme.workflowConfigMap);
        const keys = Object.keys(workflowConfigMap);

        // 找出复制的工作流
        const needCreates = keys
          .map(key => {
            const isCopy = workflowConfigMap[key].isCopy;

            return isCopy ? { objectId: workflowConfigMap[key].objectId, itemType: key } : null;
          })
          .filter(Boolean);

        // 生成新的工作流
        if (needCreates.length) {
          const workflowPromises = needCreates.map(async ({ objectId, itemType }) => {
            const targetWorkflow = workflowList.find(workflow => workflow.objectId === objectId);
            const workflow = await createWorkflow({
              ...pick(targetWorkflow, [
                'name',
                'description',
                'nodes',
                'transitions',
                'step',
                'initial',
                'releaseStatus',
              ]),
            });
            workflowConfigMap[itemType].objectId = workflow.id;
          });

          await Promise.all(workflowPromises);
        }

        // 创建新的工作流方案
        const workflowSchemeObject = new WorkflowScheme();
        workflowSchemeObject.set('name', values.workflowSchemeName);
        const workflowScheme = await workflowSchemeObject.save();

        // 更新workflowSchemeConfig
        const promises = keys.map(itemTypeKey => {
          const obj = new WorkflowSchemeConfig();
          // 设置工作流
          obj.set('workflow', Workflow.createWithoutData(workflowConfigMap[itemTypeKey].objectId).toPointer());

          //默认的不需要设置事项类型
          if (itemTypeKey !== '__default__') {
            obj.set('itemType', ItemType.createWithoutData(itemTypeKey).toPointer());
          }

          obj.set('workflowScheme', WorkflowScheme.createWithoutData(workflowScheme.id).toPointer());

          return obj.save();
        });
        await Promise.all(promises);

        workflowSchemePointer = WorkflowScheme.createWithoutData(workflowScheme.id).toPointer();
      }

      templateObject.set('itemTypeScheme', itemTypeSchemePointer);
      templateObject.set('itemTypeScreenScheme', itemTypeScreenSchemePointer);
      templateObject.set('workflowScheme', workflowSchemePointer);

      await templateObject.save();
      message.success(t('global.saveSuccess'));

      router.replace(`/${tenant}/settings/workspace-template`);
    } catch (err) {
      message.error(err?.message);
      console.error(err);
    }
  }, [form, changeMap, workspaceTemplate, workflowList, screenList, screenDifference, router, tenant, t]);

  const handleNext = useCallback(async () => {
    const { screen, workflow } = changeMap;
    if (currentStep < saveTimelines.length - 1) {
      try {
        await form.validateFields();
        let step = 1;
        if (currentStep === SAVE_STEP_ENUM.HIERARCHY) {
          if (!screen) {
            step += 1;
            if (!workflow) {
              step += 1;
            }
          }
        } else if (currentStep === SAVE_STEP_ENUM.SCREEN) {
          if (!workflow) {
            step += 1;
          }
        }

        return setCurrentStep(currentStep + step);
      } catch (err) {
        console.error(err);
      }
    } else {
      handleSubmit();
    }
  }, [currentStep, handleSubmit, changeMap, form]);

  const handleCancel = () => {
    setShowSavePage(false);
  };

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      const { hierarchy, screen, workflow } = changeMap;

      let step = -1;

      switch (currentStep) {
        case SAVE_STEP_ENUM.CONFIRM:
          if (!workflow) {
            step -= 1;
            if (!screen) {
              step -= 1;
            }
          }
          break;
        case SAVE_STEP_ENUM.WORKFLOW:
          if (!screen) {
            step -= 1;
          }
          break;
        case SAVE_STEP_ENUM.SCREEN:
          if (!hierarchy) {
            step = 0;
          }
          break;
        default:
          break;
      }

      setCurrentStep(currentStep + step);
    }
  }, [currentStep, changeMap]);

  useEffect(() => {
    const init = () => {
      const { hierarchy, screen, workflow } = changeMap;

      const step = hierarchy
        ? SAVE_STEP_ENUM.HIERARCHY
        : screen
          ? SAVE_STEP_ENUM.SCREEN
          : workflow
            ? SAVE_STEP_ENUM.WORKFLOW
            : SAVE_STEP_ENUM.CONFIRM;

      setCurrentStep(step);
    };

    init();
  }, [changeMap]);

  useEffect(() => {
    if (!changeMap?.screen) return;
    const result = getDifference(
      sourceWorkspaceTemplate?.itemTypeScreenScheme?.screenConfigMap,
      workspaceTemplate?.itemTypeScreenScheme?.screenConfigMap,
    );

    setScreenDifference(result || {});
  }, [workspaceTemplate?.itemTypeScreenScheme, sourceWorkspaceTemplate?.itemTypeScreenScheme, changeMap?.screen]);

  useEffect(() => {
    if (!changeMap?.workflow) return;
    const result = getDifference(
      sourceWorkspaceTemplate?.workflowScheme?.workflowScheme,
      workspaceTemplate?.workflowScheme?.workflowScheme,
    );

    setWorkflowDifference(result || {});
  }, [workspaceTemplate?.workflowScheme, sourceWorkspaceTemplate?.workflowScheme, changeMap?.workflow]);

  useEffect(() => {
    if (form && currentStep === SAVE_STEP_ENUM.CONFIRM) {
      setFormValues(form.getFieldsValue(true));
    }
  }, [form, currentStep]);

  return (
    <div className={cx('save-page')}>
      <div className={cx('save-page-side')}>
        <div className={cx('save-page-header')}>{t('pages.workspaceTemplate.setting.header.saveConfig')}</div>

        <Timeline className={cx('timeline-wrapper')}>
          {saveTimelines.map((timeline, index) => (
            <Timeline.Item
              key={timeline.key}
              className={cx({ 'timeline-gray': currentStep < index })}
              dot={
                <span
                  className={cx('timeline-dot', {
                    'timeline-dot-active': currentStep === index,
                    'timeline-dot-complete': currentStep > index,
                  })}
                >
                  {currentStep > index ? <Icons.CheckOutlined /> : index + 1}
                </span>
              }
            >
              <div>{timeline.text}</div>
              {!changeMap?.[timeline.type] && currentStep > timeline.key && (
                <div className={cx('skip-tip')}>{t('pages.workspaceTemplate.setting.savePage.skipText')}</div>
              )}
            </Timeline.Item>
          ))}
        </Timeline>
      </div>
      <div className={cx('save-page-content')}>
        <div className={cx('save-page-content-header')}>
          {t(`pages.fieldBehavior.batch.stepList.${currentStep}`)}:{currentTimeline.text}
        </div>
        <div className={cx('save-page-content-tip')}>{currentTimeline.tip}</div>
        <div className={cx('save-page-content-inner')}>
          <Form name="save" form={form} layout="vertical">
            {currentStep === SAVE_STEP_ENUM.HIERARCHY && (
              <>
                <Form.Item
                  name="hierarchyName"
                  label={t('pages.workspaceTemplate.setting.savePage.hierarchyInputLabel')}
                  rules={hierarchyRules}
                >
                  <Input placeholder={t('pages.workspaceTemplate.setting.savePage.hierarchyInputLabel')} />
                </Form.Item>
              </>
            )}
            {currentStep === SAVE_STEP_ENUM.SCREEN && (
              <>
                <Form.Item
                  name="itemTypeScreenName"
                  label={t('pages.workspaceTemplate.setting.savePage.itemTypeScreenInputLabel')}
                  rules={itemTypeScreenRules}
                >
                  <Input placeholder={t('pages.workspaceTemplate.setting.savePage.itemTypeScreenInputLabel')} />
                </Form.Item>
                {Object.keys(screenDifference).map(key => (
                  <Form.Item
                    key={key}
                    name={['screenSchemeName', key]}
                    label={
                      <div className={cx('select-label')}>
                        {t('pages.workspaceTemplate.setting.savePage.screenInputLabel')}

                        {itemTypeMap?.[key] && (
                          <span className={cx('item-type')}>
                            （{t('pages.workspaceTemplate.setting.common.linked')}
                            <ItemIcon icon={itemTypeMap[key].icon} label={itemTypeMap[key].name} />）
                          </span>
                        )}
                      </div>
                    }
                    rules={[
                      {
                        required: true,
                        validator: async (_, value) => {
                          const error = commonValidate(value);
                          if (error) return Promise.reject(error);

                          const values = form.getFieldValue('screenSchemeName');

                          const keys = Object.keys(values).filter(k => k !== key);

                          if (keys.some(k => values[k] === value)) {
                            return Promise.reject(t('validate.haveExistedName.0'));
                          }

                          return countName(ScreenScheme, value);
                        },
                      },
                    ]}
                  >
                    <Input placeholder={t('pages.workspaceTemplate.setting.savePage.screenInputLabel')} />
                  </Form.Item>
                ))}
              </>
            )}
            {currentStep === SAVE_STEP_ENUM.WORKFLOW && (
              <>
                <Form.Item
                  name="workflowSchemeName"
                  label={t('pages.workspaceTemplate.setting.savePage.workflowInputLabel')}
                  rules={workflowRules}
                >
                  <Input placeholder={t('pages.workspaceTemplate.setting.savePage.workflowInputLabel')} />
                </Form.Item>
              </>
            )}
            {currentStep === SAVE_STEP_ENUM.CONFIRM && (
              <div className={cx('confirm-box')}>
                <div className={cx('confirm-tip')}>{t('pages.workspaceTemplate.setting.savePage.confirmTip')}</div>
                <div className={cx('confirm-inner')}>
                  <div className={cx('item')}>
                    <span className={cx('label')}>{t('global.workspaces.itemTypeHierarchyScheme')}:</span>
                    <span className={cx('value')}>
                      {changeMap?.hierarchy ? formValues.hierarchyName : workspaceTemplate?.itemTypeScheme?.name}
                    </span>
                  </div>
                  <div className={cx('item')}>
                    <span className={cx('label')}>{t('global.workspaces.itemTypeScreenScheme')}:</span>
                    <span className={cx('value')}>
                      {changeMap?.screen
                        ? formValues?.itemTypeScreenName
                        : workspaceTemplate?.itemTypeScreenScheme?.name}
                    </span>
                  </div>
                  {formValues?.screenSchemeName && (
                    <div className={cx('item')}>
                      <span className={cx('label')}>{t('pages.screen.scheme.default.screenScheme')}:</span>
                      <div className={cx('value')}>
                        {Object.keys(formValues?.screenSchemeName).map(key => (
                          <div key={key}>
                            <span> {formValues?.screenSchemeName[key]}</span>
                            {itemTypeMap?.[key] && (
                              <span className={cx('item-type', 'inline')}>
                                （{t('pages.workspaceTemplate.setting.common.linked')}
                                <ItemIcon icon={itemTypeMap[key].icon} label={itemTypeMap[key].name} />）
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className={cx('item')}>
                    <span className={cx('label')}>{t('global.workspaces.workflowScheme')}:</span>
                    <span className={cx('value')}>
                      {changeMap?.workflow ? formValues?.workflowSchemeName : workspaceTemplate?.workflowScheme?.name}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </Form>
        </div>
        <div className={cx('save-page-content-footer')}>
          <Button onClick={handleCancel}>{t('global.cancel')}</Button>
          {showPrev && <Button onClick={handlePrev}>{t('global.preStep')}</Button>}
          <Button type="primary" onClick={handleNext}>
            {currentStep === saveTimelines.length - 1
              ? t('pages.workspaceTemplate.setting.savePage.confirm')
              : t('global.nextStep')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SavePage;
